import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowPathIcon, ClipboardDocumentListIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../context/ProjectsContext';
import { projectService } from '../services/projectService';
import { useRefreshTicketCounts } from '../hooks/useRefreshTicketCounts';

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function getDurationWeeks(start, end) {
  if (!start || !end) return null;
  const startDate = new Date(start);
  const endDate = new Date(end);
  const diffMs = endDate - startDate;
  if (diffMs <= 0) return null;
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24 * 7));
}

export default function PlanOverview() {
  const navigate = useNavigate();
  const { projects, loading, fetchProjects, setProjects } = useProjects();

  useEffect(() => {
    if (projects.length === 0) {
      fetchProjects().catch((err) => toast.error(err.message || 'Failed to load projects'));
    }
  }, [fetchProjects, projects.length]);

  useRefreshTicketCounts(projects, setProjects);
  
  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Plan Overview</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            High-level planning information for all open projects
          </p>
        </div>
        <button
          type="button"
          onClick={() => navigate('/projects')}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-200 shadow-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
        >
          ← Back to Projects
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-12 text-slate-500 dark:text-slate-400">
          <ArrowPathIcon className="size-5 animate-spin" />
          <span className="text-sm">Loading projects...</span>
        </div>
      ) : projects.length === 0 ? (
        <div className="rounded-xl border-2 border-dashed border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/50 p-12 text-center">
          <p className="text-slate-600 dark:text-slate-400">No projects found.</p>
        </div>
      ) : (
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map((project) => {
            const weeks = getDurationWeeks(project.start_date, project.end_date);
            const hasTickets = (project.ticketCount ?? 0) > 0;

            return (
              <div
                key={project.id}
                className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
              >
                {/* Card Header */}
                <div className="p-5 flex-1">
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="min-w-0">
                      <h3 className="text-lg font-semibold text-slate-900 dark:text-white truncate" title={project.name}>
                        {project.name}
                      </h3>
                      <span className="inline-block mt-1 px-2 py-0.5 rounded text-xs font-medium bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                        {project.jira_project_key || project.key}
                      </span>
                    </div>
                  </div>

                  {/* Details */}
                  <div className="space-y-2 mt-3">
                    {/* Ticket count */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Total tickets</span>
                      {hasTickets ? (
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {project.ticketCount}
                        </span>
                      ) : (
                        <span className="text-amber-600 dark:text-amber-400 font-medium text-xs">
                          No tickets yet
                        </span>
                      )}
                    </div>

                    {/* Date range */}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">Start date</span>
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {formatDate(project.start_date)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-slate-500 dark:text-slate-400">End date</span>
                      <span className="font-medium text-slate-800 dark:text-slate-100">
                        {formatDate(project.end_date)}
                      </span>
                    </div>

                    {/* Duration */}
                    {weeks && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">Duration</span>
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {weeks} week{weeks !== 1 ? 's' : ''}
                        </span>
                      </div>
                    )}

                    {/* Dev capacity */}
                    {project.dev_capacity_hours_per_month && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-500 dark:text-slate-400">Dev capacity</span>
                        <span className="font-medium text-slate-800 dark:text-slate-100">
                          {project.dev_capacity_hours_per_month}h / month
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer */}
                <div className="px-5 pb-5 pt-3 border-t border-slate-100 dark:border-slate-700">
                  <button
                    type="button"
                    onClick={() => navigate(`/projects/${project.id}/plan`)}
                    className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium rounded-lg bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors"
                  >
                    <ClipboardDocumentListIcon className="size-4" />
                    Open Plan View
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}